import type { Line } from './lines'

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i
// Indonesian + international phone shapes: +62..., 08..., grouped digits with
// optional spaces/separators after the country/area code.
const PHONE_PATTERN = /(?:\+?62|0)[\d\s().-]{7,}\d/
const LINKEDIN_PATTERN = /linkedin\.com\/[a-z0-9/_-]+/i

const BULLET_PREFIX = /^\s*[•·▪‣◦●○*\-–—]\s+/

export function isBulletLine(text: string): boolean {
  return BULLET_PREFIX.test(text)
}

export function stripBullet(text: string): string {
  return text.replace(BULLET_PREFIX, '').trim()
}

export type Contact = {
  email: boolean
  phone: boolean
  linkedin: boolean
}

export function detectContact(fullText: string): Contact {
  return {
    email: EMAIL_PATTERN.test(fullText),
    phone: PHONE_PATTERN.test(fullText),
    linkedin: LINKEDIN_PATTERN.test(fullText),
  }
}

// Date-format families we can tell apart mechanically. ATS parsers prefer one
// consistent format throughout.
type DateFormat = 'monthName' | 'numericSlash' | 'yearOnly'

const MONTH_NAME_PATTERN =
  /\b(jan(uari)?|feb(ruari)?|mar(et|ch)?|apr(il)?|mei|may|jun(i|e)?|jul(i|y)?|agu(stus)?|aug(ust)?|sep(tember)?|okt(ober)?|oct(ober)?|nov(ember)?|des(ember)?|dec(ember)?)\b/gi
const NUMERIC_SLASH_PATTERN = /\b\d{1,2}\/\d{4}\b/g
const YEAR_RANGE_PATTERN = /\b(19|20)\d{2}\s*[-–—]\s*((19|20)\d{2}|sekarang|present|now)\b/gi

export type DateAnalysis = {
  total: number
  consistent: boolean
}

export function analyzeDates(fullText: string): DateAnalysis {
  const counts: Record<DateFormat, number> = {
    monthName: (fullText.match(MONTH_NAME_PATTERN) ?? []).length,
    numericSlash: (fullText.match(NUMERIC_SLASH_PATTERN) ?? []).length,
    yearOnly: (fullText.match(YEAR_RANGE_PATTERN) ?? []).length,
  }

  const total = counts.monthName + counts.numericSlash + counts.yearOnly
  if (total === 0) return { total: 0, consistent: false }

  const dominant = Math.max(counts.monthName, counts.numericSlash, counts.yearOnly)
  // One format should clearly dominate (>=70%) to count as consistent.
  return { total, consistent: dominant / total >= 0.7 }
}

export type BulletAnalysis = {
  bulletCount: number
  quantifiedCount: number
  quantifiedRatio: number
}

export function analyzeBullets(lines: Line[]): BulletAnalysis {
  const bulletLines = lines.filter((line) => BULLET_PREFIX.test(line.text))
  const bulletCount = bulletLines.length
  const quantifiedCount = bulletLines.filter((line) =>
    /\d|%|persen|rb\b|ribu|juta|jt\b/i.test(line.text),
  ).length

  return {
    bulletCount,
    quantifiedCount,
    quantifiedRatio: bulletCount === 0 ? 0 : quantifiedCount / bulletCount,
  }
}

export function countWords(fullText: string): number {
  const trimmed = fullText.trim()
  if (!trimmed) return 0
  return trimmed.split(/\s+/).length
}

export function joinLines(lines: Line[]): string {
  return lines.map((line) => line.text).join('\n')
}
